/**
 * Group Redux Slice
 *
 * Manages group state with normalized data structure
 * for optimal performance and caching.
 */

import {
  createSlice,
  createAsyncThunk,
  type PayloadAction,
  type UnknownAction,
} from "@reduxjs/toolkit";
// Import directly from groupService to avoid module resolution issues
import {
  groupService,
  type CreateGroupData,
  type UpdateGroupData,
} from "../../services/groupService";
import type { WorkspaceWithGroups } from "../../services/workspaceService";
import { errorMessage } from "@jkast90/kast-common/format";
import { cache } from "../../utils/cacheUtils";
import { normalizeTile, type NormalizedGroup } from "../../utils/dataUtils";
import { workspaceService } from "../../services/workspaceService";
import type { RootState } from "../store";
import { loadAllWorkspaces } from "./workspaceSlice";

const createGroupThunk = createAsyncThunk.withTypes<{
  state: RootState;
  rejectValue: string;
}>();

// The cache key workspaceService stores workspaces-with-groups under.
const WORKSPACES_WITH_GROUPS_KEY = "workspaces-with-groups";

interface GroupState {
  groups: NormalizedGroup[];
  selectedGroup: number | null;
  loading: boolean;
  error: string | null;

  // Normalized structure
  entities: Record<number, NormalizedGroup>;
  ids: number[];

  // Metadata
  lastFetched: number | null;
  cacheValid: boolean;

  // Operation-specific states
  operations: {
    list: { loading: boolean; error: string | null };
    details: { loading: boolean; error: string | null };
    create: { loading: boolean; error: string | null };
    update: { loading: boolean; error: string | null };
    delete: { loading: boolean; error: string | null };
  };
}

// Hydrate from the signed-in user's cached workspaces (which carry their
// groups inline) so groups render instantly on page load instead of waiting
// for the API round-trip. Computed lazily, so a user change (which resets this
// slice) re-reads the new user's cache instead of keeping the first one's.
function buildInitialState(): GroupState {
  const cachedWorkspaces = cache.get<WorkspaceWithGroups[]>(
    WORKSPACES_WITH_GROUPS_KEY,
    { ttl: Infinity },
  );
  const groups = Array.isArray(cachedWorkspaces)
    ? deriveGroupsFromWorkspaces(cachedWorkspaces)
    : [];
  const entities: Record<number, NormalizedGroup> = {};
  const ids: number[] = [];
  groups.forEach((group) => {
    entities[group.id] = group;
    ids.push(group.id);
  });

  return {
    groups,
    selectedGroup: null,
    loading: false,
    error: null,

    entities,
    ids,

    lastFetched: null,
    cacheValid: groups.length > 0,

    operations: {
      list: { loading: false, error: null },
      details: { loading: false, error: null },
      create: { loading: false, error: null },
      update: { loading: false, error: null },
      delete: { loading: false, error: null },
    },
  };
}

// Async thunks
export const loadGroups = createGroupThunk(
  "groups/loadGroups",
  async (_, { rejectWithValue }) => {
    try {
      return await groupService.getGroups();
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Failed to load groups"));
    }
  },
);

// Derive a deduplicated list of groups from a list of workspaces-with-groups.
// Groups can appear in multiple workspaces (shared); we keep the first
// occurrence so the dedup is stable.
export function deriveGroupsFromWorkspaces(
  allWorkspaces: readonly WorkspaceWithGroups[] | null | undefined,
): NormalizedGroup[] {
  const seen = new Set<number>();
  const out: NormalizedGroup[] = [];
  for (const ws of allWorkspaces || []) {
    for (const group of ws.groups || []) {
      const id = group.group_id ?? group.id;
      if (id == null || seen.has(id)) continue;
      seen.add(id);
      out.push({
        ...group,
        id,
        group_id: id,
        tiles: group.tiles || [],
        notes: group.notes || [],
      });
    }
  }
  return out;
}

export const loadGroupsWithDetails = createGroupThunk(
  "groups/loadGroupsWithDetails",
  async (
    payload: { forceRefresh?: boolean } | undefined,
    { dispatch, getState, rejectWithValue },
  ) => {
    try {
      const forceRefresh = payload?.forceRefresh;
      let allWorkspaces = getState().workspace?.allWorkspaces || [];

      if (forceRefresh || allWorkspaces.length === 0) {
        await dispatch(loadAllWorkspaces({ forceRefresh })).unwrap();
        allWorkspaces = getState().workspace?.allWorkspaces || [];
      }

      return deriveGroupsFromWorkspaces(allWorkspaces);
    } catch (error) {
      return rejectWithValue(
        errorMessage(error, "Failed to load groups with details"),
      );
    }
  },
);

export const loadGroup = createGroupThunk(
  "groups/loadGroup",
  async (groupId: number, { rejectWithValue }) => {
    try {
      return await groupService.getGroup(groupId);
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Failed to load group"));
    }
  },
);

/**
 * Load groups from workspace - efficient single API call
 * This replaces the N+1 query pattern of loadGroupsWithDetails
 */
export const loadGroupsFromWorkspace = createGroupThunk(
  "groups/loadGroupsFromWorkspace",
  async (workspaceId: number, { rejectWithValue }) => {
    try {
      const workspaceData =
        await workspaceService.getWorkspaceDetails(workspaceId);

      // Extract and normalize groups from workspace data
      // IMPORTANT: Normalize tiles to ensure consistent ID field (tile_id -> id)
      // This prevents duplicates when the same link has different key fields
      const linkGroups = (workspaceData.tile_groups_v1 || []).map(
        (group): NormalizedGroup => ({
          ...group,
          group_id: group.id,
          group_name: group.name,
          group_type: "links",
          tiles: (group.tiles || []).map(normalizeTile),
          notes: [],
        }),
      );

      const noteGroups = (workspaceData.note_groups_v1 || []).map(
        (group): NormalizedGroup => ({
          ...group,
          group_id: group.id,
          group_name: group.name,
          group_type: "notes",
          tiles: [],
          notes: group.notes || [],
        }),
      );

      return [...linkGroups, ...noteGroups];
    } catch (error) {
      return rejectWithValue(
        errorMessage(error, "Failed to load workspace groups"),
      );
    }
  },
);

export const createGroup = createGroupThunk(
  "groups/createGroup",
  async (
    { groupData, userEmail }: { groupData: CreateGroupData; userEmail: string },
    { rejectWithValue },
  ) => {
    try {
      return await groupService.createGroup(groupData, userEmail);
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Failed to create group"));
    }
  },
);

export const updateGroup = createGroupThunk(
  "groups/updateGroup",
  async (
    { groupId, groupData }: { groupId: number; groupData: UpdateGroupData },
    { rejectWithValue },
  ) => {
    try {
      return await groupService.updateGroup(groupId, groupData);
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Failed to update group"));
    }
  },
);

export const deleteGroup = createGroupThunk(
  "groups/deleteGroup",
  async (groupId: number, { rejectWithValue }) => {
    try {
      await groupService.deleteGroup(groupId);
      return groupId;
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Failed to delete group"));
    }
  },
);

const groupSlice = createSlice({
  name: "groups",
  initialState: buildInitialState,
  reducers: {
    setSelectedGroup: (state, action: PayloadAction<number | null>) => {
      state.selectedGroup = action.payload;
    },
    setGroups: (state, action: PayloadAction<NormalizedGroup[]>) => {
      state.groups = action.payload;

      // Update normalized structure
      state.entities = {};
      state.ids = [];
      action.payload.forEach((group) => {
        state.entities[group.id] = group;
        state.ids.push(group.id);
      });
    },
    setAllGroups: (state, action: PayloadAction<NormalizedGroup[]>) => {
      // Alias for setGroups to maintain compatibility
      state.groups = action.payload;

      // Update normalized structure
      state.entities = {};
      state.ids = [];
      action.payload.forEach((group) => {
        state.entities[group.id] = group;
        state.ids.push(group.id);
      });
    },
    updateGroupInList: (
      state,
      action: PayloadAction<{
        groupId: number;
        updates: Partial<NormalizedGroup>;
      }>,
    ) => {
      const { groupId, updates } = action.payload;

      // Update in normalized entities
      if (state.entities[groupId]) {
        state.entities[groupId] = {
          ...state.entities[groupId],
          ...updates,
        };
      }

      // Update in array
      const index = state.groups.findIndex((g) => g.id === groupId);
      if (index !== -1) {
        state.groups[index] = { ...state.groups[index], ...updates };
      }
    },
    removeGroupFromList: (state, action: PayloadAction<number>) => {
      const groupId = action.payload;

      // Remove from normalized structure
      delete state.entities[groupId];
      state.ids = state.ids.filter((id) => id !== groupId);

      // Remove from array
      state.groups = state.groups.filter((g) => g.id !== groupId);

      // Clear selection if deleted group was selected
      if (state.selectedGroup === groupId) {
        state.selectedGroup = null;
      }
    },
    clearError: (state) => {
      state.error = null;
      // Clear all operation errors
      Object.values(state.operations).forEach((op) => {
        op.error = null;
      });
    },
    setCacheValidity: (state, action: PayloadAction<boolean>) => {
      state.cacheValid = action.payload;
      if (!action.payload) {
        state.lastFetched = null;
      }
    },
    setLoading: (
      state,
      action: PayloadAction<{ operation: string; loading: boolean }>,
    ) => {
      const { operation, loading } = action.payload;
      if (operation === "fetchAll") {
        state.loading = loading;
        state.operations.list.loading = loading;
      } else if (operation in state.operations) {
        state.operations[operation as keyof typeof state.operations].loading =
          loading;
      }
    },
    setError: (
      state,
      action: PayloadAction<{ operation: string; error: string }>,
    ) => {
      const { operation, error } = action.payload;
      if (operation === "fetchAll") {
        state.error = error;
        state.operations.list.error = error;
      } else if (operation in state.operations) {
        state.operations[operation as keyof typeof state.operations].error =
          error;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Load groups
      .addCase(loadGroups.pending, (state) => {
        state.loading = true;
        state.operations.list.loading = true;
      })
      .addCase(loadGroups.fulfilled, (state, action) => {
        state.loading = false;
        state.operations.list.loading = false;
        state.groups = action.payload;

        // Update normalized structure
        state.entities = {};
        state.ids = [];
        action.payload.forEach((group) => {
          state.entities[group.id] = group;
          state.ids.push(group.id);
        });

        state.lastFetched = Date.now();
        state.cacheValid = true;
      })
      .addCase(loadGroups.rejected, (state, action) => {
        state.loading = false;
        state.operations.list.loading = false;
        state.error = action.payload ?? null;
        state.operations.list.error = action.payload ?? null;
      })

      // Load groups with details — only flash loading on cold load. When we
      // already have groups (from cache hydration), revalidate silently.
      .addCase(loadGroupsWithDetails.pending, (state) => {
        if (state.groups.length === 0) {
          state.loading = true;
          state.operations.list.loading = true;
        }
      })
      .addCase(loadGroupsWithDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.operations.list.loading = false;
        // Store groups with their tiles/notes included
        state.groups = action.payload;

        // Update normalized structure
        state.entities = {};
        state.ids = [];
        action.payload.forEach((group) => {
          const groupId = group.group_id || group.id;
          // Store the full group with tiles and notes
          state.entities[groupId] = {
            ...group,
            id: groupId,
            tiles: group.tiles || [],
            notes: group.notes || [],
          };
          state.ids.push(groupId);
        });

        state.lastFetched = Date.now();
        state.cacheValid = true;
      })
      .addCase(loadGroupsWithDetails.rejected, (state, action) => {
        state.loading = false;
        state.operations.list.loading = false;
        state.error = action.payload ?? null;
        state.operations.list.error = action.payload ?? null;
      })

      // Load groups from workspace (efficient single API call)
      .addCase(loadGroupsFromWorkspace.pending, (state) => {
        state.loading = true;
        state.operations.list.loading = true;
      })
      .addCase(loadGroupsFromWorkspace.fulfilled, (state, action) => {
        state.loading = false;
        state.operations.list.loading = false;
        // Store groups with their tiles/notes included
        state.groups = action.payload;

        // Update normalized structure
        state.entities = {};
        state.ids = [];
        action.payload.forEach((group) => {
          const groupId = group.group_id || group.id;
          state.entities[groupId] = {
            ...group,
            id: groupId,
            tiles: group.tiles || [],
            notes: group.notes || [],
          };
          state.ids.push(groupId);
        });

        state.lastFetched = Date.now();
        state.cacheValid = true;
      })
      .addCase(loadGroupsFromWorkspace.rejected, (state, action) => {
        state.loading = false;
        state.operations.list.loading = false;
        state.error = action.payload ?? null;
        state.operations.list.error = action.payload ?? null;
      })

      // Load single group
      .addCase(loadGroup.pending, (state) => {
        state.operations.details.loading = true;
      })
      .addCase(loadGroup.fulfilled, (state, action) => {
        state.operations.details.loading = false;
        const group = action.payload;

        // Update in entities
        state.entities[group.id] = group;

        // Add to ids if not present
        if (!state.ids.includes(group.id)) {
          state.ids.push(group.id);
        }

        // Update in array
        const index = state.groups.findIndex((g) => g.id === group.id);
        if (index !== -1) {
          state.groups[index] = group;
        } else {
          state.groups.push(group);
        }
      })
      .addCase(loadGroup.rejected, (state, action) => {
        state.operations.details.loading = false;
        state.operations.details.error = action.payload ?? null;
      })

      // Create group
      .addCase(createGroup.pending, (state) => {
        state.operations.create.loading = true;
      })
      .addCase(createGroup.fulfilled, (state, action) => {
        state.operations.create.loading = false;
        const group = action.payload;

        state.groups.push(group);
        state.entities[group.id] = group;
        state.ids.push(group.id);
      })
      .addCase(createGroup.rejected, (state, action) => {
        state.operations.create.loading = false;
        state.operations.create.error = action.payload ?? null;
      })

      // Update group
      .addCase(updateGroup.pending, (state) => {
        state.operations.update.loading = true;
      })
      .addCase(updateGroup.fulfilled, (state, action) => {
        state.operations.update.loading = false;
        const updatedGroup = action.payload;

        // Update in entities
        state.entities[updatedGroup.id] = updatedGroup;

        // Update in array
        const index = state.groups.findIndex((g) => g.id === updatedGroup.id);
        if (index !== -1) {
          state.groups[index] = updatedGroup;
        }
      })
      .addCase(updateGroup.rejected, (state, action) => {
        state.operations.update.loading = false;
        state.operations.update.error = action.payload ?? null;
      })

      // Delete group
      .addCase(deleteGroup.pending, (state) => {
        state.operations.delete.loading = true;
      })
      .addCase(deleteGroup.fulfilled, (state, action) => {
        state.operations.delete.loading = false;
        const groupId = action.payload;

        // Remove from all structures
        delete state.entities[groupId];
        state.ids = state.ids.filter((id) => id !== groupId);
        state.groups = state.groups.filter((g) => g.id !== groupId);

        // Clear selection if needed
        if (state.selectedGroup === groupId) {
          state.selectedGroup = null;
        }
      })
      .addCase(deleteGroup.rejected, (state, action) => {
        state.operations.delete.loading = false;
        state.operations.delete.error = action.payload ?? null;
      })
      // Cross-slice: whenever workspaces (with embedded groups) arrive,
      // derive groupSlice contents so consumers of
      // useSelector(state.groups.groups) stay in sync without a separate
      // /groups API call. That's both the thunk's result and a background
      // revalidation (setAllWorkspaces, dispatched from onFreshData).
      //
      // Matched by action-type string instead of `loadAllWorkspaces.fulfilled`
      // so this module doesn't have to dereference the thunk at module-eval
      // time (groupSlice and workspaceSlice import each other). The next page
      // load hydrates from the workspaces cache workspaceService writes.
      .addMatcher(
        (
          action: UnknownAction,
        ): action is PayloadAction<WorkspaceWithGroups[] | undefined> =>
          action?.type === "workspace/loadAllWorkspaces/fulfilled" ||
          action?.type === "workspace/setAllWorkspaces",
        (state, action) => {
          const groups = deriveGroupsFromWorkspaces(action.payload || []);
          state.groups = groups;
          state.entities = {};
          state.ids = [];
          groups.forEach((group) => {
            const groupId = group.group_id ?? group.id;
            state.entities[groupId] = group;
            state.ids.push(groupId);
          });
          state.lastFetched = Date.now();
          state.cacheValid = true;
        },
      );
  },
});

// Export actions
export const {
  setSelectedGroup,
  setGroups,
  setAllGroups,
  updateGroupInList,
  removeGroupFromList,
  clearError,
  setCacheValidity,
  setLoading,
  setError,
} = groupSlice.actions;

// Export actions with groupActions namespace for compatibility
export const groupActions = groupSlice.actions;

// Selectors
export const selectGroups = (state: RootState) => state.groups.groups;
export const selectSelectedGroup = (state: RootState) =>
  state.groups.selectedGroup;
export const selectGroupsLoading = (state: RootState) => state.groups.loading;
export const selectGroupsError = (state: RootState) => state.groups.error;
export const selectGroupEntities = (state: RootState) => state.groups.entities;
export const selectGroupIds = (state: RootState) => state.groups.ids;

// Cache selectors
export const selectGroupsCacheValid = (state: RootState) =>
  state.groups.cacheValid;
export const selectGroupsLastFetched = (state: RootState) =>
  state.groups.lastFetched;

// Cache TTL - 5 minutes
const GROUPS_CACHE_TTL = 5 * 60 * 1000;

export const selectGroupsCacheExpired = (state: RootState) => {
  const lastFetched = state.groups.lastFetched;
  if (!lastFetched) return true;
  return Date.now() - lastFetched > GROUPS_CACHE_TTL;
};

// Helper selector to determine if we should fetch groups
export const selectShouldFetchGroups = (state: RootState) => {
  const hasGroups = state.groups.groups.length > 0;
  const cacheExpired = selectGroupsCacheExpired(state);
  return !hasGroups || cacheExpired;
};

// Export reducer
export default groupSlice.reducer;
